import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

declare global {
  interface Window {
    authContext?: any;
  }
}

interface AuthContextType {
  isAuthenticated: boolean;
  userId: number | null;
  login: (email: string, password: string, redirectPath?: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string, redirectPath?: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => void;
  authenticateWithGoogle: () => Promise<void>;
  authenticateWithToken: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [hasFetchedUser, setHasFetchedUser] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (token && !hasFetchedUser && !isFetchingUser) {
      setIsAuthenticated(true);
      setIsFetchingUser(true);
      
      // Fetch user info only once
      authService.getCurrentUser()
        .then(userInfo => {
          setUserId(userInfo.id);
          setHasFetchedUser(true);
        })
        .catch((error) => {
          console.error('Failed to fetch user info:', error);
          setUserId(null);
          // Reset flags on error so we can retry
          setHasFetchedUser(false);
        })
        .finally(() => {
          setIsFetchingUser(false);
        });
    } else if (!token) {
      // Clear all auth state when no token
      setIsAuthenticated(false);
      setUserId(null);
      setHasFetchedUser(false);
      setIsFetchingUser(false);
    }
  }, [hasFetchedUser, isFetchingUser]);

  const login = async (email: string, password: string, redirectPath: string = '/') => {
    try {
      const response = await authService.login(email, password);
      if (response.access_token) {
        setIsAuthenticated(true);
        setIsFetchingUser(true);
        
        // Fetch user info immediately after login
        const userInfo = await authService.getCurrentUser();
        setUserId(userInfo.id);
        setHasFetchedUser(true); // Mark as fetched
        setIsFetchingUser(false);
        
        navigate(redirectPath); // Navigate to the intended destination
      }
    } catch (error) {
      console.error('Login error:', error);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, redirectPath: string = '/') => {
    try {
      const response = await authService.register(email, password, firstName, lastName);
      if (response.access_token) {
        setIsAuthenticated(true);
        setIsFetchingUser(true);
        
        // Fetch user info immediately after registration
        const userInfo = await authService.getCurrentUser();
        setUserId(userInfo.id);
        setHasFetchedUser(true); // Mark as fetched
        setIsFetchingUser(false);
        
        navigate(redirectPath); // Navigate to the intended destination
      }
    } catch (error) {
      console.error('Registration error:', error);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await authService.forgotPassword(email);
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await authService.resetPassword(token, newPassword);
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    // Clear all auth state
    setIsAuthenticated(false);
    setUserId(null);
    setHasFetchedUser(false);
    navigate('/login');
  };

  const authenticateWithGoogle = async () => {
    try {
      const response = await authService.handleGoogleCallback();
      if (response.access_token) {
        setIsAuthenticated(true);
        setIsFetchingUser(true);
        
        // Fetch user info immediately after Google auth
        const userInfo = await authService.getCurrentUser();
        setUserId(userInfo.id);
        setHasFetchedUser(true); // Mark as fetched
        setIsFetchingUser(false);
      }
    } catch (error) {
      console.error('Google authentication error:', error);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const authenticateWithToken = async (token: string) => {
    try {
      // Store the token
      localStorage.setItem('token', token);
      
      // Update auth state
      setIsAuthenticated(true);
      setIsFetchingUser(true);
      
      // Fetch user info immediately
      const userInfo = await authService.getCurrentUser();
      setUserId(userInfo.id);
      setHasFetchedUser(true); // Mark as fetched
      setIsFetchingUser(false);
    } catch (error) {
      console.error('Token authentication error:', error);
      setIsFetchingUser(false);
      throw error;
    }
  };

  const contextValue = { isAuthenticated, userId, login, register, forgotPassword, resetPassword, changePassword, logout, authenticateWithGoogle, authenticateWithToken };

  // Expose to window for debugging
  if (typeof window !== "undefined") {
    window.authContext = contextValue;
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
